// author: Caleb Yearsley (caleb.yearsley@tecuity.com)
// date: 2019-09-18

import * as React from "react";

// import "./OrderedSectionView.less";

import { AuthorizedComponent, GradientBar } from "components/general";

import { OrderedSectionApi } from "lib/api";
import { IBaseProps } from "lib/data/interfaces";
import { OrderedSection, Permission, Section, SectionVersion } from "lib/data/model";

import { notification, Badge, Button } from "antd";
import { useMissingTemplate } from "lib/hooks";
import { cloneDeep, curry, __ } from "lodash/fp";

interface IOrderedSectionViewProps extends IBaseProps {
	orderedSection: OrderedSection;
	parentSection: Section;
	sectionVersion: SectionVersion;
	requiredPermission: Permission;
	isDraft: boolean;
	currentVersion: SectionVersion;
	onSuccessfulDelete?: (orderedSection: OrderedSection) => void;
	onClick: (orderedSection: OrderedSection) => void;
	highlightWords?: string[];
}

export const OrderedSectionView = (props: IOrderedSectionViewProps) => {

	const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
	const [sectionActions, setSectionActions] = React.useState<Array<React.ReactElement<Button | AuthorizedComponent<Button>>> | undefined>(undefined);
	const { component, warning, isOrphaned, setComponent } = useMissingTemplate(props.orderedSection, props.parentSection, props.currentVersion);

	const className = `ordered-section-view${props.className ? ` ${props.className}` : ""}`;

	const sectionName = (component.item.published !== undefined ? component.item.published.name : component.item.draft!.name);
	const sectionClassName = (component.item.published !== undefined ? "section-view-section-bar" : "section-view-section-bar draft");



	React.useEffect(() => {
		setComponent(props.orderedSection);
	}, [props.orderedSection, setComponent]);

	React.useEffect(() => {

		const onSectionDelete = async (_event: React.MouseEvent, child: OrderedSection) => {

			// Clone Current Version
			const clonedVersion = cloneDeep(props.currentVersion);

			// Locate Child In Draft
			const foundChildIndex = clonedVersion.sections.findIndex((existingOrderedSection) => existingOrderedSection.item.id === child.item.id);

			// Check Found Child
			if (foundChildIndex !== -1) {

				// Indicate Section Is Deleting
				setIsDeleting(true);

				// Remove Child From Draft
				clonedVersion.sections.splice(foundChildIndex, 1);

				// Delete Section Group
				const isDeleted = await OrderedSectionApi.delete(props.parentSection, child);

				// Check Updated Draft
				if (isDeleted === true) {

					// Process Updated Draft's Children
					if (props.onSuccessfulDelete !== undefined) {

						props.onSuccessfulDelete(component);
					}

					notification.success({
						message: "Delete successful"
					});
				}
				else {

					notification.error({
						message: "Failed to delete"
					});
					setIsDeleting(false);
				}
			}
		};

		if (props.isDraft) {

			setSectionActions([
				(
					<AuthorizedComponent
						permission={props.requiredPermission}
						key="section-delete"
					>
						<Button
							icon="delete"
							type="ghost"
							loading={isDeleting}
							onClick={curry(onSectionDelete)(__, component)}
						/>
					</AuthorizedComponent>
				)
			]);
		}
		else {

			setSectionActions(undefined);
		}
	}, [isDeleting, component, props]);

	const onSectionClick = () => {

		if (props.onClick !== undefined) {

			props.onClick(component);
		}
	};

	// Return Rendered Section
	return (
		<section className={className}>
			<GradientBar
				className={sectionClassName}
				size="medium"
				title={sectionName}
				titleAppendage={(
					component.item.published === undefined
						?
						<Badge offset={[25, -7.5]} count={<div className="unpublished-section-badge">unpublished</div>} />
						:
						null
				)}
				onClick={onSectionClick}
				highlightWords={props.highlightWords}
				actions={sectionActions}
			>
				{
					isOrphaned && props.isDraft === true
						?
						warning
						:
						undefined
				}
			</GradientBar>
		</section>
	);
};
